/**
 * The page's cooldown knowledge (ADR-073's amendment for P2.1-T4b): each ability's ready tick in server ticks, and
 * the page's estimate of the tick an act resolves at. An act resolves on the tick the input it follows is drained
 * (ADR-073 decision 3), so the estimate maps an input's `seq` to a tick by the smallest `tick - ackSeq` the own
 * snapshots of the last second carried. The server still validates every act; this only keeps the page from sending
 * one it knows would be refused for cooldown. While the page's own chain runs (ADR-073's amendment "the Strike combo
 * and the Heavy Strike chain", item 6), every ability waits until its root ends, guessed at sending and confirmed by
 * `used` as a cooldown is. Everything is preallocated, so nothing here allocates after construction.
 *
 * @example
 * ```ts
 * readiness.acknowledge(record.tick, record.ackSeq);
 * if (readiness.ticksUntilReady(strike, body.inputSeq) <= 0) {
 *   readiness.sent(strike, afterSeq, cooldownTicks);
 * }
 * ```
 */

/**
 * How early a click may come and still fire, in ticks before its ability is ready (200 ms).
 */
export const BUFFER_TICKS = 4;

/**
 * How many of the latest acknowledgements the tick estimate keeps its smallest offset over: a second of snapshots.
 */
export const OFFSET_WINDOW = 20;

/**
 * The ticks a ready tick guessed at sending waits beyond the rules' cooldown until `used` gives the real one: a
 * click lands between two inputs and may resolve a tick after the input it follows.
 */
export const PROVISIONAL_GUARD_TICKS = 1;

/**
 * Ready ticks and the tick estimate.
 */
export class Readiness {
  private readonly ready: Float64Array;
  private readonly confirmed: Float64Array;
  private readonly provisional: Float64Array;
  private readonly awaiting: Int32Array;
  private readonly offsets = new Float64Array(OFFSET_WINDOW);
  private offsetCount = 0;
  private offsetNext = 0;
  private offset = 0;
  private busyUntil = 0;
  private busyConfirmed = 0;
  private busyProvisional = 0;

  /**
   * @param abilities How many abilities the rules hold; ability numbers run from 1 to it.
   */
  public constructor(abilities: number) {
    this.ready = new Float64Array(abilities + 1);
    this.confirmed = new Float64Array(abilities + 1);
    this.provisional = new Float64Array(abilities + 1);
    this.awaiting = new Int32Array(abilities + 1);
  }

  /**
   * Reads an own snapshot's acknowledgement: the input `ackSeq` had been drained by `tick`. An acknowledgement of
   * no input (0) is skipped.
   *
   * @param tick The snapshot's tick.
   * @param ackSeq The last input seq the server had applied by it.
   */
  public acknowledge(tick: number, ackSeq: number): void {
    if (ackSeq <= 0) {
      return;
    }
    this.offsets[this.offsetNext] = tick - ackSeq;
    this.offsetNext = (this.offsetNext + 1) % OFFSET_WINDOW;
    this.offsetCount = Math.min(OFFSET_WINDOW, this.offsetCount + 1);
    let smallest = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.offsetCount; i += 1) {
      smallest = Math.min(smallest, this.offsets[i] as number);
    }
    this.offset = smallest;
  }

  /**
   * The estimated tick an input is drained at, and so an act following it resolves at.
   *
   * @param seq The input's seq.
   * @returns The tick.
   */
  public tickOf(seq: number): number {
    return seq + this.offset;
  }

  /**
   * How many ticks an act following an input would come before its ability is ready.
   *
   * @param ability The ability's number.
   * @param seq The input the act would follow.
   * @returns The ticks to wait, 0 when ready or when no acknowledgement has arrived yet. A chain's wait the tick has
   *   passed is ended here, so `busy` holds only while one runs.
   */
  public ticksUntilReady(ability: number, seq: number): number {
    if (this.offsetCount === 0) {
      return 0;
    }
    if (this.busyUntil > 0 && this.tickOf(seq) >= this.busyUntil) {
      this.endBusy();
    }
    return Math.max(0, Math.max(this.ready[ability] as number, this.busyUntil) - this.tickOf(seq));
  }

  /**
   * The ready tick the page holds for an ability: the last `used` gave it, or the guess at sending while one waits.
   *
   * @param ability The ability's number.
   * @returns The tick, 0 before any use.
   */
  public readyTick(ability: number): number {
    return this.ready[ability] as number;
  }

  /**
   * Records an act sent: its ability is guessed ready the rules' cooldown after the tick it should resolve at, plus
   * the guard, until its `used` arrives.
   *
   * @param ability The ability's number.
   * @param afterSeq The input the act follows.
   * @param cooldownTicks The rules' cooldown.
   * @param rootTicks The root of the ability's chain, during which every ability waits; 0 for none.
   */
  public sent(ability: number, afterSeq: number, cooldownTicks: number, rootTicks = 0): void {
    this.awaiting[ability] = (this.awaiting[ability] as number) + 1;
    if (this.offsetCount === 0) {
      return;
    }
    const guess = this.tickOf(afterSeq) + cooldownTicks + PROVISIONAL_GUARD_TICKS;
    this.provisional[ability] = guess;
    this.ready[ability] = Math.max(this.ready[ability] as number, guess);
    if (rootTicks > 0) {
      this.busyProvisional = this.tickOf(afterSeq) + rootTicks + PROVISIONAL_GUARD_TICKS;
      this.busyUntil = Math.max(this.busyUntil, this.busyProvisional);
    }
  }

  /**
   * Reads a `used`: the ability is ready its cooldown after the frame's tick; while a newer act still waits for its
   * answer, the later of that and the newer act's guess.
   *
   * @param ability The ability's number.
   * @param tick The combat frame's tick.
   * @param cooldownTicks The event's cooldown.
   * @param rootTicks The root of the ability's chain, 0 for none: every ability waits until the frame's tick plus it.
   */
  public used(ability: number, tick: number, cooldownTicks: number, rootTicks = 0): void {
    const waiting = Math.max(0, (this.awaiting[ability] as number) - 1);
    this.awaiting[ability] = waiting;
    const confirmed = tick + cooldownTicks;
    this.confirmed[ability] = confirmed;
    this.ready[ability] = waiting > 0 ? Math.max(confirmed, this.provisional[ability] as number) : confirmed;
    if (rootTicks > 0) {
      this.busyConfirmed = tick + rootTicks;
      this.busyUntil = waiting > 0 ? Math.max(this.busyConfirmed, this.busyProvisional) : this.busyConfirmed;
    }
  }

  /**
   * Reads a `cooldown` told at a join (ADR-084's note for P2.3-T8): the ability is ready its ticks after the frame's
   * tick, or later while an act the page sent still waits for its answer. It answers no act, so nothing awaited changes.
   *
   * @param ability The ability's number.
   * @param tick The combat frame's tick.
   * @param cooldownTicks The event's ticks until ready.
   */
  public told(ability: number, tick: number, cooldownTicks: number): void {
    const confirmed = tick + cooldownTicks;
    this.confirmed[ability] = confirmed;
    this.ready[ability] = (this.awaiting[ability] as number) > 0 ? Math.max(confirmed, this.provisional[ability] as number) : confirmed;
  }

  /**
   * Reads a `refused`: when it answers the newest act the page awaits, the ability's ready tick goes back to the last
   * one confirmed.
   *
   * A refused chain's root goes back to the last one confirmed with it.
   *
   * @param ability The ability's number.
   * @param rootTicks The root of the ability's chain, 0 for none.
   * @returns True when the refused act was the newest the page awaited, whose swing is the one playing.
   */
  public refused(ability: number, rootTicks = 0): boolean {
    const awaited = this.awaiting[ability] as number;
    if (awaited === 0) {
      return false;
    }
    this.awaiting[ability] = awaited - 1;
    if (awaited > 1) {
      return false;
    }
    this.ready[ability] = this.confirmed[ability] as number;
    if (rootTicks > 0) {
      this.busyUntil = this.busyConfirmed;
    }
    return true;
  }

  /**
   * Whether the page holds a chain's wait, sent or confirmed and not yet ended.
   *
   * @returns True while one is held.
   */
  public get busy(): boolean {
    return this.busyUntil > 0;
  }

  /**
   * Ends the chain's wait at once, as a stun or a death ends the chain on the map server.
   */
  public endBusy(): void {
    this.busyUntil = 0;
    this.busyConfirmed = 0;
    this.busyProvisional = 0;
  }

  /**
   * Forgets every ready tick, act awaited and acknowledgement, as a session ends.
   */
  public reset(): void {
    this.ready.fill(0);
    this.confirmed.fill(0);
    this.provisional.fill(0);
    this.awaiting.fill(0);
    this.offsetCount = 0;
    this.offsetNext = 0;
    this.offset = 0;
    this.endBusy();
  }
}
